import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, getComicsByCategory, type ComicCategory } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const comics = getComicsByCategory(slug || 'all');

  useEffect(() => {
    document.title = `${currentCategory.name}韩漫 - 韩漫在线观看`;
  }, [currentCategory]);

  const displayCategories = categories.filter((c) => c.slug !== 'all');

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-24 pb-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="mb-6" aria-label="面包屑导航">
            <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              </li>
              <li className="text-foreground-300">/</li>
              <li className="text-foreground-700">{currentCategory.name}韩漫</li>
            </ol>
          </nav>

          <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
            {currentCategory.slug === 'all' ? '全部韩漫作品' : `${currentCategory.name}韩漫`}
          </h1>
          <p className="text-sm text-foreground-600 mb-8">
            {currentCategory.description} · 共 {comics.length} 部作品
          </p>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat: ComicCategory) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-label whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  slug === cat.slug || (!slug && cat.slug === 'all')
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 border border-background-200/70'
                }`}
              >
                <i className={cat.icon} style={{ width: '14px', height: '14px' }}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Comic Grid */}
          {comics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
              {comics.map((comic) => (
                <Link
                  key={comic.slug}
                  to={`/comic/${comic.slug}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      title={`${comic.title} - ${currentCategory.name}韩漫`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-500 text-white text-xs rounded-full font-label">
                        <i className="ri-star-fill text-[10px]" style={{ width: '10px', height: '10px' }}></i>
                        {comic.rating}
                      </span>
                    </div>
                    {comic.status === '已完结' && (
                      <div className="absolute top-2 right-2">
                        <span className="inline-flex px-2 py-0.5 bg-accent-500 text-white text-xs rounded-full font-label">
                          完结
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 className="font-heading text-sm md:text-base text-foreground-900 group-hover:text-primary-500 transition-colors truncate mb-1">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 truncate">{comic.author}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line" style={{ width: '12px', height: '12px' }}></i>
                        {(comic.views / 10000).toFixed(1)}万
                      </span>
                      <span>{comic.status}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400" style={{ width: '32px', height: '32px' }}></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画作品</p>
              <Link
                to="/category/all"
                className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 cursor-pointer"
              >
                浏览全部漫画
              </Link>
            </div>
          )}
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hanmancomic.com/' },
              { '@type': 'ListItem', position: 2, name: `${currentCategory.name}韩漫`, item: `https://www.hanmancomic.com/category/${slug || 'all'}` },
            ],
          }),
        }}
      />
    </>
  );
}